import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';

/* Approval engine (P1 §5.6, ADR-0023). */

export const APPROVAL_DOMAINS = [
  'purchase_request',
  'purchase_order',
  'supplier_invoice',
  'ap_payment',
] as const;
export const approvalDomainSchema = z.enum(APPROVAL_DOMAINS);
export type ApprovalDomain = z.infer<typeof approvalDomainSchema>;

export const approvalStepDefSchema = z.object({
  permission: z.string().min(1).max(64),
  /** System or tenant role code the approver must hold; null = any role with the permission. */
  role: z.string().min(1).max(40).nullable(),
  label: z.string().trim().min(1).max(60),
});

export const approvalPolicySchema = z.object({
  id: uuidSchema,
  branch_id: uuidSchema.nullable(),
  domain: approvalDomainSchema,
  name: z.string(),
  min_amount_minor: z.number().int(),
  max_amount_minor: z.number().int().nullable(),
  steps: z.array(approvalStepDefSchema),
  active: z.boolean(),
  version: z.number().int(),
});
export type ApprovalPolicy = z.infer<typeof approvalPolicySchema>;
export const approvalPolicyListResponseSchema = z.object({ items: z.array(approvalPolicySchema) });

const amountSchema = z.number().int().min(0).refine(Number.isSafeInteger);
const policyFields = {
  branch_id: uuidSchema.nullable(),
  name: z.string().trim().min(1).max(80),
  min_amount_minor: amountSchema,
  max_amount_minor: amountSchema.nullable(),
  steps: z.array(approvalStepDefSchema).min(1).max(5),
};
export const createApprovalPolicyRequestSchema = z.object({
  domain: approvalDomainSchema,
  ...policyFields,
  branch_id: policyFields.branch_id.default(null),
  min_amount_minor: policyFields.min_amount_minor.default(0),
  max_amount_minor: policyFields.max_amount_minor.default(null),
});
/** Edits apply to new submissions only; pending requests keep their snapshot. */
export const updateApprovalPolicyRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  ...z.object(policyFields).partial().shape,
  active: z.boolean().optional(),
});

export const approvalStepSchema = z.object({
  step_no: z.number().int(),
  label: z.string(),
  permission: z.string(),
  role: z.string().nullable(),
  status: z.enum(['waiting', 'pending', 'approved', 'rejected', 'cancelled']),
  assigned_user_id: uuidSchema.nullable(),
  assigned_user_name: z.string().nullable(),
  escalation_reason: z.string().nullable(),
  decided_by_name: z.string().nullable(),
  on_behalf_of_name: z.string().nullable(),
  decided_at: isoTimestampSchema.nullable(),
  note: z.string().nullable(),
});

export const approvalRequestSchema = z.object({
  id: uuidSchema,
  domain: approvalDomainSchema,
  target_type: z.string(),
  target_id: uuidSchema,
  target_label: z.string(),
  target_version: z.number().int(),
  amount_minor: z.number().int(),
  currency: z.string(),
  status: z.enum(['pending', 'approved', 'rejected', 'cancelled']),
  current_step: z.number().int(),
  policy_name: z.string(),
  policy_version: z.number().int(),
  initiator_id: uuidSchema,
  initiator_name: z.string().nullable(),
  created_at: isoTimestampSchema,
  decided_at: isoTimestampSchema.nullable(),
  version: z.number().int(),
  steps: z.array(approvalStepSchema),
  /** Whether the caller may decide the current step (and why not). */
  can_decide: z.boolean(),
  refusal: z.string().nullable(),
});
export type ApprovalRequest = z.infer<typeof approvalRequestSchema>;
export const approvalRequestListResponseSchema = z.object({
  items: z.array(approvalRequestSchema),
});
export const approvalListQuerySchema = z.object({
  /** `decided` = approved, rejected or cancelled. */
  status: z.enum(['pending', 'approved', 'rejected', 'cancelled', 'decided']).optional(),
  target_type: z.string().max(40).optional(),
  target_id: uuidSchema.optional(),
});

export const approvalDecisionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  note: z.string().trim().min(1).max(300).optional(),
});
export const approvalEscalateRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  to_user_id: uuidSchema,
  reason: z.string().trim().min(3).max(300),
});

/** One pending decision for the caller, across the approval engine, waste and counts. */
export const inboxItemSchema = z.object({
  kind: z.enum(['approval_request', 'waste', 'stock_count']),
  id: uuidSchema,
  /** Approval domain, or `waste` / `stock_count`. */
  domain: z.string(),
  label: z.string(),
  amount_minor: z.number().int().nullable(),
  currency: z.string(),
  step_label: z.string().nullable(),
  submitted_by_name: z.string().nullable(),
  submitted_at: isoTimestampSchema.nullable(),
  target_type: z.string(),
  target_id: uuidSchema,
});
export type InboxItem = z.infer<typeof inboxItemSchema>;
export const inboxResponseSchema = z.object({ items: z.array(inboxItemSchema) });

export const approvalDelegationSchema = z.object({
  id: uuidSchema,
  branch_id: uuidSchema.nullable(),
  delegator_user_id: uuidSchema,
  delegator_name: z.string().nullable(),
  delegate_user_id: uuidSchema,
  delegate_name: z.string().nullable(),
  valid_from: isoTimestampSchema,
  valid_to: isoTimestampSchema,
  reason: z.string(),
  revoked_at: isoTimestampSchema.nullable(),
  active: z.boolean(),
});
export const approvalDelegationListResponseSchema = z.object({
  items: z.array(approvalDelegationSchema),
});
export const createApprovalDelegationRequestSchema = z.object({
  delegate_user_id: uuidSchema,
  branch_id: uuidSchema.nullable().default(null),
  valid_from: isoTimestampSchema,
  valid_to: isoTimestampSchema,
  reason: z.string().trim().min(3).max(300),
});

/** Staff who can be chosen as delegate or escalation target. */
export const approverCandidateListResponseSchema = z.object({
  items: z.array(z.object({ user_id: uuidSchema, display_name: z.string() })),
});
